import type { ElementType, ReactNode } from "react";
import React from "react";
import type { TFunction } from "i18next";
import { FaChild, FaHome, FaMoneyBillWave, FaUserFriends } from "react-icons/fa";
import { familySituationLabel, hasSeparateHouseholds } from "../../../utils/familySituation";
import type { Family, FamilyDetailsTab, StatusTone } from "../types";

const initialsFor = (name: string | null | undefined): string =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("") || "?";

interface FamilyHeaderProps {
  family: Family;
  childrenCount: number;
  connectedParentsCount: number;
  t: TFunction;
}

export const FamilyHeader = ({ family, childrenCount, connectedParentsCount, t }: FamilyHeaderProps) => (
  <header className="relative overflow-hidden rounded-[24px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/70 p-5 shadow-[0_18px_45px_rgba(67,56,202,0.12)] dark:border-indigo-400/25 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20 sm:p-6">
    <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] opacity-75 lg:block">
      <div className="absolute bottom-4 right-7 h-20 w-48 rounded-t-full bg-sky-100/75 dark:bg-sky-300/10" />
      <div className="absolute bottom-4 right-24 h-28 w-40 rounded-t-full bg-indigo-100/70 dark:bg-indigo-300/10" />
      <div className="absolute bottom-8 right-44 grid grid-cols-5 gap-2 opacity-55">
        {Array.from({ length: 25 }).map((_, index) => (
          <span key={index} className="h-1.5 w-1.5 rounded-full bg-indigo-300/70 dark:bg-indigo-200/30" />
        ))}
      </div>
    </div>
    <div className="relative flex flex-col gap-5 md:flex-row md:items-center">
      <div className="relative flex h-28 w-28 shrink-0 items-center justify-center rounded-full border-[5px] border-white bg-indigo-50 text-3xl font-bold uppercase text-indigo-700 shadow-xl shadow-indigo-900/10 dark:border-white/20 dark:bg-indigo-400/15 dark:text-indigo-100 sm:h-32 sm:w-32">
        {initialsFor(family.familyName)}
      </div>
      <div className="min-w-0">
        <h1 className="break-words text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
          {family.familyName || t("family.details.title", { defaultValue: "Family details" })}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${
              hasSeparateHouseholds(family.familySituation)
                ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/40 dark:bg-emerald-400/15 dark:text-emerald-100"
            }`}
          >
            {familySituationLabel(t, family.familySituation)}
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
            <FaUserFriends aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
            {t("family.details.parents", { defaultValue: "Parents" })}: {connectedParentsCount}
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
            <FaChild aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
            {t("family.details.children", { defaultValue: "Children" })}: {childrenCount}
          </span>
        </div>
      </div>
    </div>
  </header>
);

interface StatCardProps {
  icon: ElementType;
  label: ReactNode;
  value: ReactNode;
  helper?: ReactNode;
  tone?: StatusTone;
}

const toneClasses: Record<StatusTone, string> = {
  indigo: "border-indigo-200/80 bg-indigo-50/55 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-950/25 dark:text-indigo-200",
  sky: "border-sky-200/80 bg-sky-50/60 text-sky-600 dark:border-sky-500/40 dark:bg-sky-950/25 dark:text-sky-200",
  amber: "border-amber-200/80 bg-amber-50/60 text-amber-600 dark:border-amber-500/40 dark:bg-amber-950/25 dark:text-amber-200",
  emerald: "border-emerald-200/80 bg-emerald-50/55 text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-950/25 dark:text-emerald-200",
  neutral: "border-slate-200/80 bg-slate-50/70 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-200",
};

export const FamilyStatCard = ({ icon: Icon, label, value, helper, tone = "indigo" }: StatCardProps) => (
  <div className={`rounded-[18px] border p-4 shadow-sm ${toneClasses[tone]}`}>
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/70 text-base shadow-sm dark:bg-white/10">
        <Icon aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-2 break-words text-base font-bold text-slate-950 dark:text-white">{value}</p>
        {helper && <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{helper}</p>}
      </div>
    </div>
  </div>
);

export const FamilyTabs = ({
  activeTab,
  onChange,
  t,
}: {
  activeTab: FamilyDetailsTab;
  onChange: (tab: FamilyDetailsTab) => void;
  t: TFunction;
}) => {
  const tabs: Array<{ id: FamilyDetailsTab; label: string; icon: ElementType }> = [
    { id: "info", label: t("family.details.basic_info", { defaultValue: "Basic information" }), icon: FaHome },
    { id: "finance", label: t("family.details.financial_management", { defaultValue: "Financial management" }), icon: FaMoneyBillWave },
  ];
  return (
    <div className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-1.5 shadow-sm dark:border-white/10 dark:bg-white/5" role="tablist">
      <div className="grid gap-1.5 sm:grid-cols-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.id)}
              className={`relative inline-flex min-h-[68px] items-center justify-center gap-3 rounded-[18px] px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 ${
                selected
                  ? "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-100 dark:ring-indigo-300/20"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
              }`}
            >
              <Icon aria-hidden="true" />
              {tab.label}
              {selected && <span className="absolute inset-x-5 bottom-1.5 h-1 rounded-full bg-indigo-600 dark:bg-indigo-300" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};
